<script setup>
import { computed, onMounted, ref } from "vue";
import UserAccountService from "@/services/UserAccountService";
import { useAuthStore } from "@/stores/AuthStore";
import { useReferenceLayout } from "@/composables/useReferenceLayout";
import { usePresentationMode } from "@/composables/usePresentationMode";

const auth = useAuthStore();
const passwords = ref({ current: "", new: "", confirm: "" });
const passwordSaving = ref(false);
const passwordNotice = ref("");
const passwordError = ref("");
const avatarPreview = ref("");
const avatarSaving = ref(false);
const avatarError = ref("");
const telegramStatus = ref(null);
const telegramLoading = ref(false);
const telegramLink = ref(null);
const telegramNotice = ref("");
const telegramError = ref("");
const notificationStatus = ref(null);
const notificationForm = ref({
  web_enabled: true,
  telegram_enabled: false,
  slack_enabled: false,
});
const notificationLoading = ref(false);
const notificationNotice = ref("");
const notificationError = ref("");
const activeSection = ref("account");
const { presentationModeEnabled } = usePresentationMode();
const { referenceLayout } = useReferenceLayout();

const settingSections = computed(() => [
  { id: "account", label: "Account", description: "Profile & login details" },
  { id: "security", label: "Security", description: "Password & access" },
  { id: "notifications", label: "Notifications", description: "Delivery channels" },
  { id: "integrations", label: "Integrations", description: "Connected services" },
  { id: "appearance", label: "Appearance", description: "Theme & display" },
]);

const initials = computed(() => {
  const name = auth.name || auth.email || "";
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) {
    return "U";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
});

const loadAvatar = async () => {
  try {
    const { data } = await UserAccountService.getAvatar();
    avatarPreview.value = data?.image || "";
  } catch (error) {
    avatarPreview.value = "";
  }
};


const handleAvatarChange = async (event) => {
  const file = event.target.files?.[0];
  if (!file) {
    return;
  }
  avatarError.value = "";
  avatarSaving.value = true;
  try {
    const { data } = await UserAccountService.uploadAvatar(file);
    avatarPreview.value = data?.image || avatarPreview.value;
    const storedUser = auth.user || {};
    const updatedUser = {
      ...storedUser,
      avatar: data?.avatar ?? storedUser.avatar,
      avatarUrl: data?.image ?? storedUser.avatarUrl,
    };
    auth.setUser(updatedUser);
  } catch (error) {
    avatarError.value = "Unable to upload avatar.";
  } finally {
    avatarSaving.value = false;
    event.target.value = "";
  }
};

const changePassword = async () => {
  passwordNotice.value = "";
  passwordError.value = "";
  passwordSaving.value = true;
  try {
    await UserAccountService.changePassword({
      current: passwords.value.current,
      new: passwords.value.new,
      new_confirmation: passwords.value.confirm,
    });
    passwords.value = { current: "", new: "", confirm: "" };
    passwordNotice.value = "Password updated.";
  } catch (error) {
    passwordError.value = "Error changing password. Please try again.";
  } finally {
    passwordSaving.value = false;
  }
};

const loadTelegramStatus = async () => {
  telegramLoading.value = true;
  telegramError.value = "";
  try {
    const { data } = await UserAccountService.getTelegramStatus();
    telegramStatus.value = data;
  } catch (error) {
    telegramError.value = "Unable to load Telegram settings.";
  } finally {
    telegramLoading.value = false;
  }
};

const createTelegramLink = async () => {
  telegramLoading.value = true;
  telegramNotice.value = "";
  telegramError.value = "";
  try {
    const { data } = await UserAccountService.createTelegramLink();
    telegramLink.value = data;
    telegramNotice.value = "A single-use link has been created. It expires shortly.";
  } catch (error) {
    telegramError.value = error?.response?.data?.message || "Unable to create a Telegram link.";
  } finally {
    telegramLoading.value = false;
  }
};

const copyTelegramCode = async () => {
  if (!telegramLink.value?.code) {
    return;
  }
  try {
    await navigator.clipboard.writeText(telegramLink.value.code);
    telegramNotice.value = "Linking code copied.";
  } catch (error) {
    telegramError.value = "Unable to copy the code. Select and copy it manually.";
  }
};

const disconnectTelegram = async () => {
  if (!window.confirm("Disconnect Telegram from your Neptune account?")) {
    return;
  }
  telegramLoading.value = true;
  telegramNotice.value = "";
  telegramError.value = "";
  try {
    await UserAccountService.disconnectTelegram();
    telegramLink.value = null;
    telegramNotice.value = "Telegram has been disconnected.";
    await loadTelegramStatus();
    await loadNotificationPreferences();
  } catch (error) {
    telegramError.value = "Unable to disconnect Telegram.";
  } finally {
    telegramLoading.value = false;
  }
};

const loadNotificationPreferences = async () => {
  notificationLoading.value = true;
  notificationError.value = "";
  try {
    const { data } = await UserAccountService.getNotificationPreferences();
    notificationStatus.value = data;
    notificationForm.value = {
      web_enabled: data?.channels?.web?.enabled ?? true,
      telegram_enabled: data?.channels?.telegram?.enabled ?? false,
      slack_enabled: data?.channels?.slack?.enabled ?? false,
    };
  } catch (error) {
    notificationError.value = "Unable to load notification preferences.";
  } finally {
    notificationLoading.value = false;
  }
};

const saveNotificationPreferences = async () => {
  notificationLoading.value = true;
  notificationNotice.value = "";
  notificationError.value = "";
  try {
    const { data } = await UserAccountService.updateNotificationPreferences(notificationForm.value);
    notificationStatus.value = data;
    notificationForm.value = {
      web_enabled: data?.channels?.web?.enabled ?? true,
      telegram_enabled: data?.channels?.telegram?.enabled ?? false,
      slack_enabled: data?.channels?.slack?.enabled ?? false,
    };
    notificationNotice.value = "Notification channels updated.";
  } catch (error) {
    const errors = error?.response?.data?.errors;
    notificationError.value = errors
      ? Object.values(errors).flat()[0]
      : error?.response?.data?.message || "Unable to update notification preferences.";
  } finally {
    notificationLoading.value = false;
  }
};

const formatTelegramDate = (value) => {
  if (!value) {
    return "";
  }
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

onMounted(() => {
  loadAvatar();
  loadTelegramStatus();
  loadNotificationPreferences();
});
</script>

<template>
  <section class="p-6 space-y-6">
    <header class="space-y-1">
      <p class="text-sm font-semibold uppercase tracking-wide text-slate-500">Settings</p>
      <h1 class="text-2xl font-bold text-slate-900 dark:text-slate-50">
        Account & App Settings
      </h1>
      <p class="text-sm text-slate-500 dark:text-slate-400">
        Manage your profile and jump into app-specific settings.
      </p>
    </header>

    <div class="grid gap-6 lg:grid-cols-[280px_1fr]">
      <aside class="space-y-4">
        <div class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div class="flex items-center gap-3">
            <div
              class="relative flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-sm font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-100">
              <img v-if="avatarPreview" :src="avatarPreview" alt="Avatar"
                class="h-full w-full object-cover object-center" />
              <span v-else>{{ initials }}</span>
            </div>
            <div>
              <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {{ auth.name || "Your profile" }}
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                {{ auth.email || "Signed in" }}
              </p>
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-slate-200 bg-white p-2 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <button
            v-for="section in settingSections"
            :key="section.id"
            class="w-full rounded-lg px-3 py-2 text-left text-sm transition"
            :class="activeSection === section.id
              ? 'bg-slate-900 text-white'
              : 'text-slate-600 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700'"
            @click="activeSection = section.id"
          >
            <div class="font-semibold">{{ section.label }}</div>
            <div class="text-xs opacity-80">{{ section.description }}</div>
          </button>
        </div>
      </aside>

      <div class="space-y-6">
        <div v-if="activeSection === 'account'" class="space-y-6">
          <FormBox header="Profile">
            <div class="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div class="flex items-center gap-4">
                <div
                  class="relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-2xl font-semibold text-slate-600 ring-2 ring-white shadow-sm dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-700">
                  <img v-if="avatarPreview" :src="avatarPreview" alt="Avatar"
                    class="h-full w-full object-cover object-center" />
                  <span v-else>{{ initials }}</span>
                </div>
                <div>
                  <p class="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {{ auth.name || "Your profile" }}
                  </p>
                  <p class="text-sm text-slate-500 dark:text-slate-400">
                    {{ auth.email || "Update your account details in future" }}
                  </p>
                </div>
              </div>
              <div class="flex-1 space-y-2">
                <label class="text-sm font-medium text-slate-600 dark:text-slate-300">
                  Profile avatar
                </label>
                <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    class="block w-full text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-800 dark:text-slate-300"
                    :disabled="avatarSaving"
                    @change="handleAvatarChange"
                  />
                  <span v-if="avatarSaving" class="text-xs text-slate-500 dark:text-slate-400">
                    Uploading…
                  </span>
                </div>
                <p v-if="avatarError" class="text-xs text-rose-600">{{ avatarError }}</p>
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  PNG, JPG, or WEBP. Uploading replaces the existing avatar.
                </p>
              </div>
            </div>
          </FormBox>
        </div>

        <div v-else-if="activeSection === 'security'" class="space-y-6">
          <FormBox header="Security">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Current password</label>
              <input class="input px-4 py-2" type="password" v-model="passwords.current" />
              <label class="text-sm font-medium text-slate-600 dark:text-slate-300">New password</label>
              <input class="input px-4 py-2" type="password" v-model="passwords.new" />
              <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Confirm password</label>
              <input class="input px-4 py-2" type="password" v-model="passwords.confirm" />
              <div></div>
              <div class="flex items-center gap-3">
                <button class="btn accent" :disabled="passwordSaving" @click="changePassword">
                  {{ passwordSaving ? "Saving…" : "Change Password" }}
                </button>
                <span v-if="passwordNotice" class="text-sm text-emerald-600">{{ passwordNotice }}</span>
                <span v-if="passwordError" class="text-sm text-rose-600">{{ passwordError }}</span>
              </div>
            </div>
          </FormBox>
        </div>

        <div v-else-if="activeSection === 'notifications'" class="space-y-6">
          <FormBox header="Notification channels">
            <div class="space-y-5">
              <div>
                <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Choose where Neptune contacts you
                </p>
                <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  These choices apply to notifications addressed to your Neptune account. You can use
                  any combination of available channels.
                </p>
              </div>

              <div v-if="notificationLoading && !notificationStatus" class="text-sm text-slate-500 dark:text-slate-400">
                Loading notification preferences…
              </div>

              <div v-else class="space-y-3">
                <label
                  class="flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
                >
                  <div>
                    <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Neptune web app
                    </p>
                    <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Show notifications in the bell menu while you use Neptune.
                    </p>
                  </div>
                  <input
                    v-model="notificationForm.web_enabled"
                    type="checkbox"
                    class="h-5 w-5 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                  />
                </label>

                <label
                  class="flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
                  :class="notificationStatus?.channels?.telegram?.available || notificationForm.telegram_enabled ? '' : 'opacity-60'"
                >
                  <div>
                    <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Telegram
                    </p>
                    <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Receive a private message from the Neptune bot. Connect Telegram under Integrations first.
                    </p>
                    <p
                      v-if="!notificationStatus?.channels?.telegram?.connected"
                      class="mt-2 text-xs font-medium text-amber-700 dark:text-amber-300"
                    >
                      Telegram is not connected.
                    </p>
                  </div>
                  <input
                    v-model="notificationForm.telegram_enabled"
                    type="checkbox"
                    :disabled="!notificationStatus?.channels?.telegram?.available && !notificationForm.telegram_enabled"
                    class="h-5 w-5 rounded border-slate-300 text-slate-900 focus:ring-slate-500 disabled:cursor-not-allowed"
                  />
                </label>

                <label
                  class="flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
                  :class="notificationStatus?.channels?.slack?.available || notificationForm.slack_enabled ? '' : 'opacity-60'"
                >
                  <div>
                    <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Slack
                    </p>
                    <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Receive a direct message in the company Slack workspace. Neptune securely matches your Slack member using {{ auth.email }}.
                    </p>
                    <p
                      v-if="!notificationStatus?.channels?.slack?.available"
                      class="mt-2 text-xs font-medium text-amber-700 dark:text-amber-300"
                    >
                      Slack notifications have not been configured by an administrator.
                    </p>
                    <p
                      v-else-if="notificationStatus?.channels?.slack?.connected"
                      class="mt-2 text-xs font-medium text-emerald-700 dark:text-emerald-300"
                    >
                      Slack identity verified.
                    </p>
                  </div>
                  <input
                    v-model="notificationForm.slack_enabled"
                    type="checkbox"
                    :disabled="!notificationStatus?.channels?.slack?.available && !notificationForm.slack_enabled"
                    class="h-5 w-5 rounded border-slate-300 text-slate-900 focus:ring-slate-500 disabled:cursor-not-allowed"
                  />
                </label>
              </div>

              <div class="flex flex-wrap items-center gap-3">
                <button class="btn accent" :disabled="notificationLoading" @click="saveNotificationPreferences">
                  {{ notificationLoading ? "Saving…" : "Save notification channels" }}
                </button>
                <span v-if="notificationNotice" class="text-sm text-emerald-600 dark:text-emerald-400">
                  {{ notificationNotice }}
                </span>
                <span v-if="notificationError" class="text-sm text-rose-600 dark:text-rose-400">
                  {{ notificationError }}
                </span>
              </div>
            </div>
          </FormBox>
        </div>

        <div v-else-if="activeSection === 'integrations'" class="space-y-6">
          <FormBox header="Telegram agent">
            <div class="space-y-5">
              <div>
                <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Secure access to Neptune from Telegram
                </p>
                <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Ask questions about tickets, customers, quotes, jobs, schedules, and calendars.
                  The Telegram agent is private, read-only, and uses your existing Neptune permissions.
                </p>
              </div>

              <div v-if="telegramLoading && !telegramStatus" class="text-sm text-slate-500 dark:text-slate-400">
                Loading Telegram settings…
              </div>

              <div
                v-else-if="telegramStatus?.linked"
                class="rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/40"
              >
                <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p class="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                      Telegram connected
                    </p>
                    <p class="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                      <span v-if="telegramStatus.account?.username">@{{ telegramStatus.account.username }} · </span>
                      Linked {{ formatTelegramDate(telegramStatus.account?.linked_at) }}
                    </p>
                    <p v-if="!telegramStatus.available" class="mt-2 text-xs text-amber-700 dark:text-amber-300">
                      Agent access is currently unavailable, but you can still disconnect this account.
                    </p>
                  </div>
                  <div class="flex gap-2">
                    <a
                      v-if="telegramStatus.bot_username"
                      class="btn accent"
                      :href="`https://t.me/${telegramStatus.bot_username}`"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open Telegram
                    </a>
                    <button class="btn" :disabled="telegramLoading" @click="disconnectTelegram">
                      Disconnect
                    </button>
                  </div>
                </div>
              </div>

              <div
                v-else-if="telegramStatus && !telegramStatus.available"
                class="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
              >
                Telegram agent access is not available for this account or has not been configured.
              </div>

              <div v-else-if="telegramStatus?.available" class="space-y-4">
                <button
                  v-if="!telegramLink"
                  class="btn accent"
                  :disabled="telegramLoading"
                  @click="createTelegramLink"
                >
                  {{ telegramLoading ? "Creating secure link…" : "Connect Telegram" }}
                </button>

                <div
                  v-else
                  class="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40"
                >
                  <div>
                    <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Single-use linking code
                    </p>
                    <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Expires {{ formatTelegramDate(telegramLink.expires_at) }}. Do not share this code.
                    </p>
                  </div>
                  <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <code class="select-all rounded-md bg-slate-900 px-4 py-2 font-mono text-base tracking-wider text-white">
                      {{ telegramLink.code }}
                    </code>
                    <button class="btn" @click="copyTelegramCode">Copy code</button>
                    <a
                      class="btn accent"
                      :href="telegramLink.deep_link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Continue in Telegram
                    </a>
                  </div>
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    Opening the link sends the code directly to the official Neptune bot. Linking works only in a private chat.
                  </p>
                </div>
              </div>

              <p v-if="telegramNotice" class="text-sm text-emerald-600 dark:text-emerald-400">
                {{ telegramNotice }}
              </p>
              <p v-if="telegramError" class="text-sm text-rose-600 dark:text-rose-400">
                {{ telegramError }}
              </p>
            </div>
          </FormBox>
        </div>

        <div v-else class="space-y-6">
          <FormBox header="Appearance">
            <fieldset class="mb-6 space-y-3" aria-describedby="reference-layout-help">
              <legend class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Reference page layout
              </legend>
              <p
                id="reference-layout-help"
                class="text-sm text-slate-500 dark:text-slate-400"
              >
                Choose how job references are displayed. Saved for your account on this
                browser.
              </p>
              <label
                class="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
              >
                <input
                  v-model="referenceLayout"
                  type="radio"
                  name="reference-layout"
                  value="new"
                  class="mt-1 h-5 w-5"
                />
                <span
                  ><span class="block font-semibold text-slate-900 dark:text-slate-100"
                    >New (default)</span
                  ><span class="text-sm text-slate-500 dark:text-slate-400"
                    >Focused tabs for overview, schedule, resources, activity and payroll
                    where available.</span
                  ></span
                >
              </label>
              <label
                class="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
              >
                <input
                  v-model="referenceLayout"
                  type="radio"
                  name="reference-layout"
                  value="classic"
                  class="mt-1 h-5 w-5"
                />
                <span
                  ><span class="block font-semibold text-slate-900 dark:text-slate-100"
                    >Classic</span
                  ><span class="text-sm text-slate-500 dark:text-slate-400"
                    >All sections on one scrolling page. Available temporarily while you
                    get used to the new layout.</span
                  ></span
                >
              </label>
            </fieldset>
            <div class="space-y-4">
              <label
                class="flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
              >
                <div>
                  <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    Presentation mode
                  </p>
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    Hide currency amounts in the UI (for example, £1263.34 displays as £#####).
                  </p>
                </div>
                <input
                  v-model="presentationModeEnabled"
                  type="checkbox"
                  class="h-5 w-5 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                />
              </label>

              <div class="space-y-2 text-sm text-slate-500 dark:text-slate-400">
                Theme controls are coming soon. We’ll make per-app themes available here.
              </div>
            </div>
          </FormBox>
        </div>
      </div>
    </div>
  </section>
</template>
